import { useId, useState } from 'react'
import type { ReactNode } from 'react'
import { usePreference } from '../hooks/usePreferences'

/**
 * An explanatory note about a metric, shown as an icon beside the title.
 *
 * <p>Collapsed to a glyph by default so it costs no vertical space, and
 * expanded on click rather than on hover — a hover tooltip is unreachable on a
 * touch device and invisible on a wall display.
 */
export function InfoIcon({ text, open, onToggle }: {
  text: string
  open: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      className={`info-icon${open ? ' is-open' : ''}`}
      aria-expanded={open}
      aria-label={open ? 'Hide explanation' : 'Show explanation'}
      title={open ? 'Hide explanation' : text}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onToggle() }}
    >
      i
    </button>
  )
}

interface CardProps {
  title: ReactNode
  /** Stable key for remembering the collapsed state. Omit to disable collapsing. */
  prefKey?: string
  meta?: ReactNode
  /** Explanatory note; renders an info toggle beside the title. */
  note?: string
  defaultCollapsed?: boolean
  bodyClassName?: string
  children: ReactNode
}

/**
 * A titled panel that can be collapsed to just its header.
 *
 * <p>The collapsed state is stored per user, so a dashboard tidied on one
 * machine stays tidy on the next. The whole header is the hit target, which is
 * why the info toggle stops propagation.
 */
export function Card({
  title, prefKey, meta, note, defaultCollapsed = false, bodyClassName, children,
}: CardProps) {
  const [storedCollapsed, setStoredCollapsed] =
    usePreference<boolean>(prefKey ? `collapsed.${prefKey}` : '__none', defaultCollapsed)
  // Without a prefKey the card is not collapsible at all.
  const collapsed = prefKey ? storedCollapsed : false

  const [noteOpen, setNoteOpen] = useState(false)
  const bodyId = useId()

  const collapsible = Boolean(prefKey)

  return (
    <div className={`card${collapsed ? ' is-collapsed' : ''}`}>
      <div
        className={`card-header${collapsible ? ' is-clickable' : ''}`}
        onClick={collapsible ? () => setStoredCollapsed(!collapsed) : undefined}
        onKeyDown={collapsible ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setStoredCollapsed(!collapsed)
          }
        } : undefined}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? !collapsed : undefined}
        aria-controls={collapsible ? bodyId : undefined}
      >
        <h3 className="card-title">
          {collapsible && (
            <span className="card-chevron" aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
          )}
          <span>{title}</span>
          {note && (
            <InfoIcon text={note} open={noteOpen} onToggle={() => setNoteOpen((v) => !v)} />
          )}
        </h3>
        {meta && <span className="card-meta" onClick={(e) => e.stopPropagation()}>{meta}</span>}
      </div>

      {!collapsed && (
        <div id={bodyId} className={bodyClassName ?? 'card-body'}>
          {note && noteOpen && (
            // A note containing newlines is code — a SQL statement carried
            // verbatim from the probe. Prose descriptions are a single
            // paragraph, so the newline is a reliable signal, and the aligned
            // columns SQL is written with only line up in a monospace font.
            <div className={`metric-note${note.includes('\n') ? ' is-code' : ''}`}>{note}</div>
          )}
          {children}
        </div>
      )}
    </div>
  )
}
